import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCalendarAlt, 
  faChevronLeft, 
  faChevronRight, 
  faEye, 
  faDownload, 
  faEllipsis,
  faFilter,
  faAngleDown
} from "@fortawesome/free-solid-svg-icons";
import { Spinner, Dropdown, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { PaginationState } from "@tanstack/react-table";
import { Link, useNavigate } from "react-router-dom";
// Import from local file instead of service
// This ensures we use the correct case for the enum values
import { InvoiceItem } from "services/invoiceService";

// Define enum locally with uppercase values matching GraphQL response
export enum InvoiceType {
  Logistic = "LOGISTIC",
  Penalty = "PENALTY",
  All = ""
}

// Helper function to determine invoice type from journal ID
export const journalToInvoiceType = (journalId: number): InvoiceType => {
  switch (journalId) {
    case 76:
      return InvoiceType.Logistic;
    case 74:
      return InvoiceType.Penalty;
    default:
      return InvoiceType.Logistic; // Default to logistic if unknown
  }
};

interface InvoicesTableProps {
  invoices: InvoiceItem[];
  loading: boolean;
  totalCount: number;
  pagination: PaginationState;
  handlePageChange: (newPageIndex: number) => void;
  formatDate: (dateStr: string) => string;
  formatCurrency: (amount: number) => string;
  onTypeFilterChange?: (type: InvoiceType | string) => void;
  onDateFilterChange?: (fromDate: string, toDate: string) => void;
  currentTypeFilter?: InvoiceType | string;
  fromDate?: string;
  toDate?: string;
}

const InvoicesTable: React.FC<InvoicesTableProps> = ({
  invoices,
  loading,
  totalCount,
  pagination,
  handlePageChange,
  formatDate,
  formatCurrency,
  onTypeFilterChange,
  currentTypeFilter = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Calculate pagination values
  const pageCount = Math.ceil(totalCount / pagination.pageSize);
  const startItem = pagination.pageIndex * pagination.pageSize + 1;
  const endItem = Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount);
  
  const viewInvoiceDetails = (invoice: InvoiceItem) => {
    navigate(`/invoices/${encodeURIComponent(invoice.ref)}`, {
      state: {
        type: invoice.type,
        journalId: invoice.journal_id
      }
    });
  };
  
  const downloadInvoice = () => {
    toast.info(t("invoices.downloadStarted"));
  };
  
  // Generate page numbers for pagination
  const renderPageNumbers = () => {
    const pages = [];
    const visiblePages = 5; // Total number of page buttons to show (excluding first/last)
    
    // Always show first page
    pages.push(
      <li key={0} className={`page-item ${pagination.pageIndex === 0 ? 'active' : ''}`}>
        <button 
          className="page-link" 
          onClick={() => handlePageChange(0)}
        >
          1
        </button>
      </li>
    );
    
    // Add ellipsis after first page if needed
    if (pagination.pageIndex > 3) {
      pages.push(
        <li key="start-ellipsis" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }
    
    // Calculate range of pages to show around current page
    let startPage = Math.max(1, pagination.pageIndex - 1);
    let endPage = Math.min(pageCount - 2, pagination.pageIndex + 1);
    
    // Adjust to show more pages if we're at the start or end
    if (pagination.pageIndex <= 2) {
      // Near start, show more pages after
      endPage = Math.min(pageCount - 2, 3);
    } else if (pagination.pageIndex >= pageCount - 3) {
      // Near end, show more pages before
      startPage = Math.max(1, pageCount - 4);
    }
    
    // Add middle page numbers
    for (let i = startPage; i <= endPage; i++) {
      // Skip if this would duplicate the first or last page
      if (i === 0 || i === pageCount - 1) continue;
      
      pages.push(
        <li key={i} className={`page-item ${pagination.pageIndex === i ? 'active' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => handlePageChange(i)}
          >
            {i + 1}
          </button>
        </li>
      );
    }
    
    // Add ellipsis before last page if needed
    if (pagination.pageIndex < pageCount - 4) {
      pages.push(
        <li key="end-ellipsis" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }
    
    // Always show last page
    if (pageCount > 1) {
      pages.push(
        <li key={pageCount - 1} className={`page-item ${pagination.pageIndex === pageCount - 1 ? 'active' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => handlePageChange(pageCount - 1)}
          >
            {pageCount}
          </button>
        </li>
      );
    }
    
    return pages;
  };
  
  return (
    <div className="mb-9">
      <div className="d-flex flex-wrap justify-content-end align-items-center mb-4">
        <div className="text-body-secondary small">
          {totalCount > 0 ? (
            <span>{t("invoices.showingRecords", {start: startItem, end: endItem, total: totalCount})}</span>
          ) : (
            <span>{t("invoices.noInvoicesFound")}</span>
          )}
        </div>
      </div>
      
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <Spinner animation="border" role="status" variant="warning">
              <span className="visually-hidden">{t("loading")}</span>
            </Spinner>
          </div>
        ) : (
          <>
            <div className="table-list">
              <div className="table-responsive scrollbar mb-3">
                <table className="table table-sm fs-9 mb-0 overflow-hidden">
                  <thead className="text-body">
                    <tr>
                      <th className="sort ps-3 pe-1 align-middle white-space-nowrap" data-sort="reference" style={{ minWidth: "6rem" }}>
                        <span className="fs-8">{t("invoices.tableHeaders.reference").toUpperCase()}</span>
                      </th>
                      <th className="sort pe-1 align-middle white-space-nowrap text-center" data-sort="type" style={{ minWidth: "6rem" }}>
                        <span className="fs-8">{t("invoices.tableHeaders.type").toUpperCase()}</span>
                      </th>
                      <th className="sort pe-1 align-middle white-space-nowrap text-center" data-sort="amount" style={{ minWidth: "12rem" }}>
                        <span className="fs-8">{t("invoices.tableHeaders.amount").toUpperCase()}</span>
                      </th>
                      <th className="sort pe-4 align-middle white-space-nowrap text-center" data-sort="date" style={{ minWidth: "10rem" }}>
                        <span className="fs-8">{t("invoices.tableHeaders.date").toUpperCase()}</span>
                      </th>
                      <th className="no-sort text-center" style={{ width: "7rem" }}>
                        <span className="fs-8">{t("invoices.tableHeaders.actions").toUpperCase()}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list">
                    {invoices.map((invoice, index) => (
                      <tr key={index} className="btn-reveal-trigger">
                        <td className="py-2 ps-3 align-middle white-space-nowrap">
                          <Link 
                            to={`/invoices/${encodeURIComponent(invoice.ref)}`}
                            state={{ type: invoice.type, journalId: invoice.journal_id }}
                            className="fw-semibold text-body"
                          >
                            {invoice.ref}
                          </Link>
                        </td>
                        <td className="py-2 align-middle text-center">
                          {/* Determine invoice type based on journal_id if needed */}
                          {(() => {
                            // First check journal_id to get the correct type
                            let badgeType; 
                            
                            if (invoice.journal_id === 76) {
                              badgeType = "primary"; // Logistic
                              return (
                                <span className="badge badge-phoenix fs-10 badge-phoenix-primary">
                                  {t("invoices.types.logistic")}
                                </span>
                              );
                            } else if (invoice.journal_id === 74) {
                              badgeType = "warning"; // Penalty
                              return (
                                <span className="badge badge-phoenix fs-10 badge-phoenix-warning">
                                  {t("invoices.types.penalty")}
                                </span>
                              );
                            } else if (invoice.ref && invoice.ref.startsWith("JVSL/")) {
                              // If reference starts with JVSL, it's a penalty
                              return (
                                <span className="badge badge-phoenix fs-10 badge-phoenix-warning">
                                  {t("invoices.types.penalty")}
                                </span>
                              );
                            } else if (invoice.ref && invoice.ref.startsWith("FBW/")) {
                              // If reference starts with FBW, it's a logistic
                              return (
                                <span className="badge badge-phoenix fs-10 badge-phoenix-primary">
                                  {t("invoices.types.logistic")}
                                </span>
                              );
                            } else {
                              // Fall back to unknown
                              return (
                                <span className="badge badge-phoenix fs-10 badge-phoenix-secondary">
                                  {t("invoices.types.unknown")}
                                </span>
                              );
                            }
                          })()}
                        </td>
                        <td className="py-2 align-middle text-center fw-semibold text-body-highlight fs-9">
                          {formatCurrency(
                            typeof invoice.total === 'number' && !isNaN(invoice.total) 
                              ? invoice.total 
                              : 0
                          )}
                        </td>
                        <td className="py-2 align-middle text-center">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-1" />
                          <span className="text-body-tertiary fs-9">{formatDate(invoice.date)}</span>
                        </td>
                        <td className="py-2 align-middle white-space-nowrap text-end">
                          <div className="dropstart position-static d-inline-block">
                            <button className="btn btn-link text-body btn-sm dropdown-toggle btn-reveal" type="button" data-bs-toggle="dropdown" data-boundary="window" aria-haspopup="true" aria-expanded="false" data-bs-reference="parent">
                              <FontAwesomeIcon icon={faEllipsis} className="fs-9" />
                            </button>
                            <div className="dropdown-menu dropdown-menu-end border py-2" aria-labelledby="dropdown-1">
                              <Link 
                                to={`/invoices/${encodeURIComponent(invoice.ref)}`} 
                                className="dropdown-item"
                              >
                                {t("invoices.buttons.view")}
                              </Link>
                              <button 
                                className="dropdown-item" 
                                onClick={downloadInvoice}
                              >
                                {t("invoices.buttons.download")}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                    {invoices.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          {t("invoices.noInvoicesFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-body-secondary small">
                  {totalCount > 0 && (
                    <span>{t("invoices.totalInvoices", {count: totalCount})}</span>
                  )}
                </div>
                
                {totalCount > 0 && (
                  <nav aria-label="Page navigation">
                    <ul className="pagination mb-0">
                      <li className={`page-item ${pagination.pageIndex === 0 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(pagination.pageIndex - 1)}
                          disabled={pagination.pageIndex === 0}
                          aria-label={t("invoices.buttons.previous")}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                      </li>
                      
                      {renderPageNumbers()}
                      
                      <li className={`page-item ${pagination.pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(pagination.pageIndex + 1)}
                          disabled={pagination.pageIndex >= pageCount - 1}
                          aria-label={t("invoices.buttons.next")}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoicesTable;
